import React, { useState, useEffect } from 'react';
import { resolveMediaUrl } from '../utils/mediaHelper';
import { Palette, Check, Sparkles, Image as ImageIcon } from 'lucide-react';

interface RobustBackgroundSectionProps {
  key?: string;
  mediaKey: string;
  localPaths: string[];
  fallbackUnsplashUrl: string;
  className?: string;
  overlayClassName?: string;
  children: React.ReactNode;
  id?: string;
}

const BACKGROUNDS_OPTIONS = [
  {
    id: 'connection-mesh',
    name: 'Cyber Mesh Glow (Attached)',
    description: 'Sleek dark connection mesh with radiant amber/orange left glow and electric cyan/blue right glow',
    url: 'custom-connection-mesh',
    thumbnailUrl: 'custom-connection-mesh',
    type: 'svg'
  },
  {
    id: 'crimson-hexagon',
    name: 'Crimson Hexagon 3D (Attached)',
    description: 'Ultra-premium dark carbon 3D hexagonal grid with glowing neon red crevices',
    url: 'custom-crimson-hexagon',
    thumbnailUrl: 'custom-crimson-hexagon',
    type: 'svg'
  },
  {
    id: 'sapphire-silk',
    name: 'Sapphire Wave 3D',
    description: 'Ultra-premium dark graphite and royal sapphire 3D fluid wave',
    url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=60&custom=true',
    thumbnailUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=150&q=80',
    type: 'photo'
  },
  {
    id: 'emerald-silk',
    name: 'Emerald Flow 3D',
    description: 'Sleek flowing 3D neon emerald waves over polished black space',
    url: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&w=1200&q=60&custom=true',
    thumbnailUrl: 'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&w=150&q=80',
    type: 'photo'
  },
  {
    id: 'crimson-silk',
    name: 'Crimson Wave 3D',
    description: 'Premium flowing 3D ruby red waves on a dark charcoal matrix',
    url: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=1200&q=60&custom=true',
    thumbnailUrl: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=150&q=80',
    type: 'photo'
  },
  {
    id: 'cyber-grid',
    name: 'Cyber Tech Grid',
    description: 'Sleek black tech panel with glowing electric-blue circuit grids',
    url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=60&custom=true',
    thumbnailUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=150&q=80',
    type: 'photo'
  },
  {
    id: 'cosmic-nebula',
    name: 'Stellar Nebula 3D',
    description: 'Deep space 3D stardust clusters and cosmic blue clouds',
    url: 'https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?auto=format&fit=crop&w=1200&q=60&custom=true',
    thumbnailUrl: 'https://images.unsplash.com/photo-1506318137071-a8e063b4bec0?auto=format&fit=crop&w=150&q=80',
    type: 'photo'
  },
  {
    id: 'svg-universe',
    name: 'Attached Celestial Art',
    description: 'Cozy celestial galaxy artwork with floating glass spheres',
    url: 'svg-fallback-universe',
    thumbnailUrl: 'https://images.unsplash.com/photo-1561542320-9a18cd340469?auto=format&fit=crop&w=150&q=80',
    type: 'svg'
  }
];

export default function RobustBackgroundSection({
  mediaKey,
  localPaths,
  fallbackUnsplashUrl,
  className = '',
  overlayClassName = 'bg-gradient-to-r from-zinc-950/85 via-zinc-950/45 to-transparent',
  children,
  id
}: RobustBackgroundSectionProps) {
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [src, setSrc] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(`cozzydine_media_${mediaKey}`);
        if (stored) {
          // Migrate previous default backgrounds to connection-mesh
          if (mediaKey === 'home-hero' && (stored.includes('photo-1618005182384-a83a8bd57fbe') || stored === 'svg-fallback-universe' || stored === 'custom-crimson-hexagon')) {
            localStorage.setItem(`cozzydine_media_${mediaKey}`, 'custom-connection-mesh');
            return 'custom-connection-mesh';
          }
          if (mediaKey === 'about-hero' && (stored.includes('about-hero') || stored.includes('photo-1597075095304') || stored.includes('unsplash.com') || stored === 'svg-fallback-universe' || !BACKGROUNDS_OPTIONS.some(bg => bg.url === stored))) {
            localStorage.setItem(`cozzydine_media_${mediaKey}`, 'custom-crimson-hexagon');
            return 'custom-crimson-hexagon';
          }
          return stored;
        }
      } catch (e) {
        console.warn(e);
      }
    }
    // Default backgrounds
    if (mediaKey === 'home-hero') {
      return 'custom-connection-mesh';
    }
    if (mediaKey === 'about-hero') {
      return 'custom-crimson-hexagon';
    }
    return localPaths.length > 0 
      ? resolveMediaUrl(localPaths[0], mediaKey)
      : resolveMediaUrl(fallbackUnsplashUrl, mediaKey);
  });

  const [showCustomizer, setShowCustomizer] = useState(false);
  const [selectedBg, setSelectedBg] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(`cozzydine_media_${mediaKey}`);
        if (stored) {
          let urlToMatch = stored;
          if (mediaKey === 'home-hero' && (stored.includes('photo-1618005182384-a83a8bd57fbe') || stored === 'svg-fallback-universe' || stored === 'custom-crimson-hexagon')) {
            urlToMatch = 'custom-connection-mesh';
          } else if (mediaKey === 'about-hero' && (stored.includes('about-hero') || stored.includes('photo-1597075095304') || stored.includes('unsplash.com') || stored === 'svg-fallback-universe' || !BACKGROUNDS_OPTIONS.some(bg => bg.url === stored))) {
            urlToMatch = 'custom-crimson-hexagon';
          }
          const match = BACKGROUNDS_OPTIONS.find(bg => bg.url === urlToMatch);
          if (match) return match.id;
        }
      } catch (e) {
        console.warn(e);
      }
    }
    return mediaKey === 'home-hero' ? 'connection-mesh' : (mediaKey === 'about-hero' ? 'crimson-hexagon' : 'svg-universe');
  });

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Reset loading state when the active source changes
  useEffect(() => {
    setIsImageLoaded(false);
  }, [src]);

  // Keep selectedBg state synchronized if src changes externally
  useEffect(() => {
    const match = BACKGROUNDS_OPTIONS.find(bg => bg.url === src);
    if (match) {
      setSelectedBg(match.id);
    } else {
      setSelectedBg('svg-universe');
    }
  }, [src]);

  const handleSelectBackground = (bg: typeof BACKGROUNDS_OPTIONS[0]) => {
    try {
      if (bg.id === 'svg-universe') {
        localStorage.setItem(`cozzydine_media_${mediaKey}`, 'svg-fallback-universe');
        setSrc('svg-fallback-universe');
      } else {
        localStorage.setItem(`cozzydine_media_${mediaKey}`, bg.url);
        setSrc(bg.url);
      }
      setSelectedBg(bg.id);
    } catch (e) {
      console.error('Error saving background selection:', e);
    }
  };

  // Generate the full list of candidate URLs
  const candidateUrls = React.useMemo(() => {
    const urls: string[] = [];
    
    // Add all specified local paths
    localPaths.forEach(path => {
      urls.push(resolveMediaUrl(path, mediaKey));
    });

    // Add fallback Unsplash URL
    urls.push(resolveMediaUrl(fallbackUnsplashUrl, mediaKey));

    return urls;
  }, [localPaths, fallbackUnsplashUrl, mediaKey]);

  useEffect(() => {
    setAttemptIndex(0);
    
    if (typeof window !== 'undefined') {
      try {
        let stored = localStorage.getItem(`cozzydine_media_${mediaKey}`);
        if (stored) {
          if (mediaKey === 'home-hero' && (stored.includes('photo-1618005182384-a83a8bd57fbe') || stored === 'svg-fallback-universe' || stored === 'custom-crimson-hexagon')) {
            stored = 'custom-connection-mesh';
            localStorage.setItem(`cozzydine_media_${mediaKey}`, 'custom-connection-mesh');
          } else if (mediaKey === 'about-hero' && (stored.includes('about-hero') || stored.includes('photo-1597075095304') || stored.includes('unsplash.com') || stored === 'svg-fallback-universe' || !BACKGROUNDS_OPTIONS.some(bg => bg.url === stored))) {
            stored = 'custom-crimson-hexagon';
            localStorage.setItem(`cozzydine_media_${mediaKey}`, 'custom-crimson-hexagon');
          }
          setSrc(stored);
          return;
        }
      } catch (e) {
        console.warn(e);
      }
    }

    if (mediaKey === 'home-hero') {
      setSrc('custom-connection-mesh');
      return;
    }
    if (mediaKey === 'about-hero') {
      setSrc('custom-crimson-hexagon');
      return;
    }

    if (candidateUrls.length > 0) {
      setSrc(candidateUrls[0]);
    }
  }, [candidateUrls, mediaKey]);

  const handleError = () => {
    if (attemptIndex + 1 < candidateUrls.length) {
      const nextIndex = attemptIndex + 1;
      setAttemptIndex(nextIndex);
      setSrc(candidateUrls[nextIndex]);
    }
  };

  // Noise texture definition to give a tactile, high-end organic finish (Hidden on mobile screens to eliminate GPU stutter)
  const renderSharedNoise = () => (
    <svg className="hidden md:block absolute inset-0 w-full h-full opacity-[0.045] pointer-events-none mix-blend-overlay" xmlns="http://www.w3.org/2000/svg">
      <filter id="cozzyNoise">
        <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="matrix" values="0 0 0 0 0   0 0 0 0 0   0 0 0 0 0  0 0 0 0.8 0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#cozzyNoise)" />
    </svg>
  );

  const renderConnectionMeshGraphic = () => {
    // Elegant low-poly node mesh representing the user's attachment
    const nodes = [
      // Left side nodes (Orange/Amber glow area)
      { id: 0, x: 60, y: 120, r: 3, color: '#f97316', pulse: true, speed: '4s' },
      { id: 1, x: 140, y: 80, r: 2, color: '#fb923c', pulse: false, speed: '5s' },
      { id: 2, x: 200, y: 190, r: 4.5, color: '#ea580c', pulse: true, speed: '3s' },
      { id: 3, x: 90, y: 280, r: 2.5, color: '#f97316', pulse: false, speed: '6s' },
      { id: 4, x: 240, y: 110, r: 3, color: '#f97316', pulse: true, speed: '4s' },
      { id: 5, x: 110, y: 440, r: 3.5, color: '#fb923c', pulse: true, speed: '5s' },
      { id: 6, x: 220, y: 350, r: 2.5, color: '#ea580c', pulse: false, speed: '3.5s' },
      { id: 7, x: 310, y: 260, r: 5, color: '#ffedd5', pulse: true, speed: '2.5s' },
      { id: 8, x: 340, y: 140, r: 3, color: '#f97316', pulse: false, speed: '4.5s' },
      { id: 9, x: 380, y: 390, r: 3.5, color: '#fb923c', pulse: true, speed: '3s' },

      // Center transition nodes (blending purple/indigo)
      { id: 10, x: 480, y: 160, r: 2.5, color: '#c084fc', pulse: false, speed: '5.5s' },
      { id: 11, x: 520, y: 330, r: 3.5, color: '#a855f7', pulse: true, speed: '4s' },
      { id: 12, x: 600, y: 110, r: 2.5, color: '#818cf8', pulse: false, speed: '6s' },
      { id: 13, x: 640, y: 420, r: 4, color: '#6366f1', pulse: true, speed: '3.5s' },
      { id: 14, x: 700, y: 210, r: 3, color: '#818cf8', pulse: false, speed: '4.5s' },

      // Right side nodes (Blue/Cyan glow area)
      { id: 15, x: 800, y: 300, r: 4.5, color: '#e0f2fe', pulse: true, speed: '2.8s' },
      { id: 16, x: 840, y: 130, r: 3, color: '#06b6d4', pulse: false, speed: '5s' },
      { id: 17, x: 900, y: 410, r: 3.5, color: '#0891b2', pulse: true, speed: '4.2s' },
      { id: 18, x: 940, y: 230, r: 4, color: '#22d3ee', pulse: true, speed: '3.2s' },
      { id: 19, x: 1000, y: 90, r: 2, color: '#0284c7', pulse: false, speed: '6.5s' },
      { id: 20, x: 1040, y: 360, r: 4.5, color: '#06b6d4', pulse: true, speed: '3.8s' },
      { id: 21, x: 1070, y: 180, r: 3, color: '#38bdf8', pulse: false, speed: '4.8s' },
      { id: 22, x: 1130, y: 430, r: 3, color: '#0284c7', pulse: true, speed: '5s' },
      { id: 23, x: 1160, y: 130, r: 2, color: '#06b6d4', pulse: false, speed: '7s' }
    ];

    const edges = [
      // Left side connections
      [0, 1], [0, 3], [1, 2], [1, 4], [2, 4], [2, 7], [3, 5], [3, 6], [5, 6],
      [6, 7], [4, 8], [7, 8], [6, 9], [7, 9], [8, 9],
      // Center connections
      [8, 10], [9, 11], [2, 10], [7, 11],
      [10, 12], [11, 13], [10, 14], [11, 14], [12, 14], [13, 14],
      // Right side connections
      [14, 15], [14, 16], [13, 17], [15, 16], [15, 17], [15, 18], [16, 18], [16, 19],
      [17, 20], [17, 21], [18, 20], [18, 21], [19, 21], [20, 22], [21, 22], [21, 23], [22, 23]
    ];

    const polygons = [
      [0, 1, 2],
      [1, 2, 4],
      [3, 5, 6],
      [2, 7, 8],
      [6, 7, 9],
      [7, 8, 9],
      [10, 12, 14],
      [11, 13, 14],
      [15, 16, 18],
      [15, 17, 18],
      [17, 20, 21],
      [18, 20, 21],
      [21, 22, 23]
    ];

    return (
      <div className="absolute inset-0 w-full h-full bg-[#040509]">
        {/* Deep, rich multi-layered vignette */}
        <div 
          className="absolute inset-0 opacity-90 mix-blend-multiply pointer-events-none" 
          style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(4, 5, 9, 0) 30%, rgba(2, 3, 5, 0.95) 100%)' }} 
        />

        <svg className="absolute inset-0 w-full h-full opacity-90 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" preserveAspectRatio="none">
          <defs>
            {/* Glowing left background radial gradient (Orange/Amber) */}
            <radialGradient id="meshOrangeGlow" cx="15%" cy="50%" r="55%">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.32" />
              <stop offset="50%" stopColor="#ea580c" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>

            {/* Glowing right background radial gradient (Cyan/Blue) */}
            <radialGradient id="meshBlueGlow" cx="85%" cy="50%" r="55%">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.28" />
              <stop offset="50%" stopColor="#0284c7" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>

            {/* Soft, beautiful center ambient background radial gradient (Purple/Indigo) */}
            <radialGradient id="meshCenterGlow" cx="50%" cy="50%" r="40%">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>

            {/* Blur filter for node glows */}
            <filter id="nodeGlowBlur" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <style>{`
            @keyframes pulseNode {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50% { transform: scale(1.3); opacity: 1; }
            }
            @keyframes pulseLine {
              0%, 100% { stroke-opacity: 0.12; }
              50% { stroke-opacity: 0.32; }
            }
            @keyframes floatMesh {
              0%, 100% { transform: translateY(0px) translateX(0px); }
              50% { transform: translateY(-8px) translateX(4px); }
            }
            .pulse-n { animation: pulseNode var(--pulse-dur, 4s) ease-in-out infinite; transform-origin: var(--tx, 0px) var(--ty, 0px); }
            .pulse-l { animation: pulseLine 6s ease-in-out infinite; }
            .floating-mesh-group { animation: floatMesh 10s ease-in-out infinite; }
          `}</style>

          {/* Background Ambient Glows */}
          <rect width="100%" height="100%" fill="#040509" />
          <rect width="100%" height="100%" fill="url(#meshOrangeGlow)" />
          <rect width="100%" height="100%" fill="url(#meshBlueGlow)" />
          <rect width="100%" height="100%" fill="url(#meshCenterGlow)" />

          {/* Floating Low-Poly Geometric Network */}
          <g className="floating-mesh-group">
            {/* 1. Low-Poly semi-transparent surface polygons */}
            {polygons.map((poly, idx) => {
              const p1 = nodes[poly[0]];
              const p2 = nodes[poly[1]];
              const p3 = nodes[poly[2]];
              const color = p1.id < 10 ? '#f97316' : p1.id < 15 ? '#a855f7' : '#06b6d4';
              return (
                <polygon
                  key={`poly-${idx}`}
                  points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`}
                  fill={color}
                  fillOpacity={0.025}
                  stroke={color}
                  strokeWidth={0.5}
                  strokeOpacity={0.05}
                />
              );
            })}

            {/* 2. Connection lines (edges) with subtle animations */}
            {edges.map((edge, idx) => {
              const p1 = nodes[edge[0]];
              const p2 = nodes[edge[1]];
              const isOrange = p1.id < 10 && p2.id < 10;
              const isBlue = p1.id >= 15 && p2.id >= 15;
              const strokeColor = isOrange 
                ? '#fdba74' 
                : isBlue 
                ? '#67e8f9' 
                : '#c084fc';
              
              return (
                <line
                  key={`edge-${idx}`}
                  x1={p1.x}
                  y1={p1.y}
                  x2={p2.x}
                  y2={p2.y}
                  stroke={strokeColor}
                  strokeWidth={1}
                  className="pulse-l"
                  style={{ animationDelay: `${(idx % 5) * 0.8}s` } as React.CSSProperties}
                />
              );
            })}

            {/* 3. Glowing Connector Nodes */}
            {nodes.map((node) => (
              <g key={`node-group-${node.id}`} style={{ '--tx': `${node.x}px`, '--ty': `${node.y}px` } as React.CSSProperties}>
                {/* Outermost soft aura glow for premium visual density */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r * 2.8}
                  fill={node.color}
                  fillOpacity={0.12}
                  filter="url(#nodeGlowBlur)"
                />
                
                {/* Secondary glow */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r * 1.8}
                  fill={node.color}
                  fillOpacity={0.25}
                  className={node.pulse ? 'pulse-n' : undefined}
                  style={{ '--pulse-dur': node.speed } as React.CSSProperties}
                />

                {/* Core bright node circle */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={node.r}
                  fill="#ffffff"
                  stroke={node.color}
                  strokeWidth={1.5}
                />
              </g>
            ))}
          </g>

          {/* Decorative subtle foreground connection particles */}
          <circle cx="150" cy="180" r="1.5" fill="#fdba74" opacity="0.4" />
          <circle cx="280" cy="90" r="1" fill="#ffffff" opacity="0.5" />
          <circle cx="950" cy="380" r="1.5" fill="#67e8f9" opacity="0.4" />
          <circle cx="1080" cy="110" r="1" fill="#ffffff" opacity="0.5" />
        </svg>
      </div>
    );
  };

  const renderCrimsonHexagonGraphic = () => (
    <div className="absolute inset-0 w-full h-full bg-zinc-950">
      {/* Deep dark radial vignette */}
      <div 
        className="absolute inset-0 opacity-85 mix-blend-multiply pointer-events-none" 
        style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(10, 10, 12, 0.2) 0%, rgba(2, 2, 3, 0.95) 100%)' }} 
      />
      
      <svg className="absolute inset-0 w-full h-full opacity-90 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Radial red glow underneath */}
          <radialGradient id="redGlowCore" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#b91c1c" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>

          {/* Gradients for Hexagons */}
          <linearGradient id="hexMetalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#27272a" />
            <stop offset="40%" stopColor="#18181b" />
            <stop offset="100%" stopColor="#09090b" />
          </linearGradient>

          {/* Soft Blur filter for glow */}
          <filter id="glowBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          {/* Hexagon Pattern Definition: Tiling cell width=104, height=180 */}
          <pattern id="hexGrid" width="104" height="180" patternUnits="userSpaceOnUse" patternTransform="scale(0.8)">
            {/* Ambient Red glow lines (crevices) with blur filter */}
            <g fill="none" stroke="#ef4444" strokeWidth="1.8" opacity="0.4" filter="url(#glowBlur)">
              {/* Center hex */}
              <polygon points="52,30 104,60 104,120 52,150 0,120 0,60" />
              {/* Corner hexes */}
              <polygon points="0,-60 52,-30 52,30 0,60 -52,30 -52,-30" />
              <polygon points="104,-60 156,-30 156,30 104,60 52,30 52,-30" />
              <polygon points="0,120 52,151 52,210 0,240 -52,210 -52,150" />
              <polygon points="104,120 156,151 156,210 104,240 52,210 52,150" />
            </g>

            {/* Bright neon laser lines */}
            <g fill="none" stroke="#f87171" strokeWidth="0.8" opacity="0.8">
              <polygon points="52,30 104,60 104,120 52,150 0,120 0,60" />
              <polygon points="0,-60 52,-30 52,30 0,60 -52,30 -52,-30" />
              <polygon points="104,-60 156,-30 156,30 104,60 52,30 52,-30" />
              <polygon points="0,120 52,151 52,210 0,240 -52,210 -52,150" />
              <polygon points="104,120 156,151 156,210 104,240 52,210 52,150" />
            </g>

            {/* 3D Metal Panels on top (Inset polygons with subtle borders) */}
            {/* Center hex tile */}
            <polygon points="52,32 101.5,61 101.5,119 52,148 2.5,119 2.5,61" fill="url(#hexMetalGrad)" stroke="#18181b" strokeWidth="1.2" />
            <polyline points="2.5,119 2.5,61 52,32 101.5,61" fill="none" stroke="#3f3f46" strokeWidth="1" opacity="0.6" />
            <polyline points="2.5,119 52,148 101.5,119 101.5,61" fill="none" stroke="#09090b" strokeWidth="1.5" opacity="0.9" />

            {/* Corner pieces */}
            {/* Top-Left hex tile */}
            <polygon points="0,-58 49.5,-29 49.5,29 0,58 -49.5,29 -49.5,-29" fill="url(#hexMetalGrad)" stroke="#18181b" strokeWidth="1.2" />
            <polyline points="-49.5,29 -49.5,-29 0,-58 49.5,-29" fill="none" stroke="#3f3f46" strokeWidth="1" opacity="0.6" />
            <polyline points="-49.5,29 0,58 49.5,29 49.5,-29" fill="none" stroke="#09090b" strokeWidth="1.5" opacity="0.9" />

            {/* Top-Right hex tile */}
            <polygon points="104,-58 153.5,-29 153.5,29 104,58 54.5,29 54.5,-29" fill="url(#hexMetalGrad)" stroke="#18181b" strokeWidth="1.2" />
            <polyline points="54.5,29 54.5,-29 104,-58 153.5,-29" fill="none" stroke="#3f3f46" strokeWidth="1" opacity="0.6" />
            <polyline points="54.5,29 104,58 153.5,29 153.5,-29" fill="none" stroke="#09090b" strokeWidth="1.5" opacity="0.9" />

            {/* Bottom-Left hex tile */}
            <polygon points="0,122 49.5,151 49.5,209 0,238 -49.5,209 -49.5,151" fill="url(#hexMetalGrad)" stroke="#18181b" strokeWidth="1.2" />
            <polyline points="-49.5,209 -49.5,151 0,122 49.5,151" fill="none" stroke="#3f3f46" strokeWidth="1" opacity="0.6" />
            <polyline points="-49.5,209 0,238 49.5,209 49.5,151" fill="none" stroke="#09090b" strokeWidth="1.5" opacity="0.9" />

            {/* Bottom-Right hex tile */}
            <polygon points="104,122 153.5,151 153.5,209 104,238 54.5,209 54.5,151" fill="url(#hexMetalGrad)" stroke="#18181b" strokeWidth="1.2" />
            <polyline points="54.5,209 54.5,151 104,122 153.5,151" fill="none" stroke="#3f3f46" strokeWidth="1" opacity="0.6" />
            <polyline points="54.5,209 104,238 153.5,209 153.5,151" fill="none" stroke="#09090b" strokeWidth="1.5" opacity="0.9" />
          </pattern>
        </defs>

        {/* Backdrop for the SVG */}
        <rect width="100%" height="100%" fill="#09090b" />

        {/* Central Ambient Glow */}
        <rect width="100%" height="100%" fill="url(#redGlowCore)" />

        {/* Light overlay highlights/hotspots for a gaming theme look */}
        <circle cx="30%" cy="40%" r="500" fill="none" stroke="#ef4444" strokeWidth="2.5" opacity="0.04" />
        <circle cx="75%" cy="60%" r="600" fill="none" stroke="#ef4444" strokeWidth="1.5" opacity="0.03" />

        {/* Repeating hexagonal grid */}
        <rect width="100%" height="100%" fill="url(#hexGrid)" />
      </svg>
    </div>
  );

  const renderHomeHeroGraphic = () => (
    <svg className="absolute inset-0 w-full h-full opacity-95 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" preserveAspectRatio="none">
      <defs>
        {/* Deep, ultra-premium galactic dark canvas */}
        <radialGradient id="darkUniverseGlow" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#0f0d1a" />
          <stop offset="60%" stopColor="#08070d" />
          <stop offset="100%" stopColor="#030206" />
        </radialGradient>
        
        {/* Soft, beautiful radial ambient gradients for the high-end photo background look */}
        <radialGradient id="ambientViolet" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ambientBlue" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
        </radialGradient>

        {/* Dynamic, organic electric blue & cyan wave gradients (Bottom-left fluid artwork) */}
        <linearGradient id="electricBlueWave" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1e40af" stopOpacity="0.95" />
          <stop offset="45%" stopColor="#2563eb" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="cyanCyanWave" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0891b2" stopOpacity="0.8" />
          <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
        </linearGradient>

        {/* Highly premium 3D Sphere Radial Shading to create a beautiful tactile look */}
        <radialGradient id="sphereGradBlue" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#a5f3fc" />
          <stop offset="25%" stopColor="#38bdf8" />
          <stop offset="65%" stopColor="#1d4ed8" />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>
        <radialGradient id="sphereGradPurple" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#f472b6" />
          <stop offset="25%" stopColor="#c084fc" />
          <stop offset="70%" stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#1e1b4b" />
        </radialGradient>
        <radialGradient id="sphereGradCyan" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#e0f2fe" />
          <stop offset="20%" stopColor="#06b6d4" />
          <stop offset="65%" stopColor="#0891b2" />
          <stop offset="100%" stopColor="#0f172a" />
        </radialGradient>

        {/* Sleek corner pill and line gradients */}
        <linearGradient id="pillGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.75" />
          <stop offset="50%" stopColor="#2563eb" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#1e1b4b" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="pillGradLight" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.15" />
        </linearGradient>
      </defs>

      <style>{`
        @keyframes floatSphere1 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-10px) translateX(5px); }
        }
        @keyframes floatSphere2 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(8px) translateX(-6px); }
        }
        @keyframes floatSphere3 {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-12px) translateX(-4px); }
        }
        .float-s1 { animation: floatSphere1 7s ease-in-out infinite; }
        .float-s2 { animation: floatSphere2 8s ease-in-out infinite; }
        .float-s3 { animation: floatSphere3 9s ease-in-out infinite; }
      `}</style>

      {/* 1. Deep Space Base Canvas */}
      <rect width="100%" height="100%" fill="url(#darkUniverseGlow)" />

      {/* 2. Soft, rich ambient radial light glows from the attached art */}
      <circle cx="520" cy="80" r="280" fill="url(#ambientViolet)" />
      <circle cx="1080" cy="220" r="240" fill="url(#ambientBlue)" />
      <circle cx="850" cy="480" r="220" fill="url(#ambientViolet)" />
      <circle cx="150" cy="450" r="250" fill="url(#ambientBlue)" />

      {/* 3. Top-Left Diagonal Pill and Line Structures */}
      <g transform="translate(-40, -40) rotate(-35)">
        {/* Thick elegant pills */}
        <rect x="0" y="40" width="420" height="48" rx="24" fill="url(#pillGradLight)" />
        <rect x="60" y="105" width="310" height="38" rx="19" fill="url(#pillGrad)" />
        <rect x="120" y="160" width="220" height="28" rx="14" fill="url(#pillGradLight)" opacity="0.8" />
        {/* Thin parallel glowing lines */}
        <line x1="-50" y1="210" x2="480" y2="210" stroke="#3b82f6" strokeWidth="1.5" opacity="0.4" />
        <line x1="20" y1="225" x2="400" y2="225" stroke="#06b6d4" strokeWidth="0.8" opacity="0.3" />
      </g>

      {/* 4. Bottom-Right Diagonal Pill and Line Structures */}
      <g transform="translate(1240, 640) rotate(-35)">
        {/* Thick elegant pills */}
        <rect x="-420" y="-88" width="420" height="48" rx="24" fill="url(#pillGradLight)" />
        <rect x="-310" y="-143" width="310" height="38" rx="19" fill="url(#pillGrad)" />
        <rect x="-220" y="-188" width="220" height="28" rx="14" fill="url(#pillGradLight)" opacity="0.8" />
        {/* Thin parallel glowing lines */}
        <line x1="-480" y1="-230" x2="50" y2="-230" stroke="#3b82f6" strokeWidth="1.5" opacity="0.4" />
        <line x1="-400" y1="-245" x2="-20" y2="-245" stroke="#06b6d4" strokeWidth="0.8" opacity="0.3" />
      </g>

      {/* 5. Graceful, elegant floating bezier lines sweeping across the dark space */}
      <path d="M 120,320 Q 300,120 580,240 T 960,110 T 1200,80" fill="none" stroke="#22d3ee" strokeWidth="1.5" opacity="0.3" strokeDasharray="3 3" />
      <path d="M -50,180 Q 250,50 500,180 T 1000,120 T 1250,200" fill="none" stroke="#818cf8" strokeWidth="1.2" opacity="0.25" />
      <path d="M 200,450 Q 500,200 820,380 T 1200,320" fill="none" stroke="#38bdf8" strokeWidth="1" opacity="0.35" />

      {/* 6. Gorgeous Fluid Organic Waves at the bottom left (Attached image artwork representation) */}
      {/* Wave Layer 1 (Primary Royal Blue gradient wave) */}
      <path d="M -20,620 L -20,410 Q 170,270 280,440 T 580,380 T 880,510 T 1220,440 L 1220,620 Z" fill="url(#electricBlueWave)" />
      {/* Wave Layer 2 (Overlapping lighter cyan wave) */}
      <path d="M -20,620 L -20,460 Q 150,330 250,470 T 540,410 T 850,530 T 1220,480 L 1220,620 Z" fill="url(#cyanCyanWave)" />

      {/* 7. Shimmering 3D Glass Spheres matching the uploaded photo's floating coordinates */}
      {/* Sphere 1 (Floating above the bottom-left wave) */}
      <circle cx="110" cy="325" r="20" fill="url(#sphereGradBlue)" className="float-s1" style={{ transformOrigin: '110px 325px' }} />
      {/* Sphere 2 (Big, elegant sphere in the upper-left) */}
      <circle cx="330" cy="155" r="35" fill="url(#sphereGradPurple)" className="float-s2" style={{ transformOrigin: '330px 155px' }} />
      {/* Sphere 3 (Large sphere in the upper-right) */}
      <circle cx="1010" cy="175" r="30" fill="url(#sphereGradBlue)" className="float-s3" style={{ transformOrigin: '1010px 175px' }} />
      {/* Sphere 4 (Floating sphere on the right side of the screen) */}
      <circle cx="910" cy="450" r="24" fill="url(#sphereGradPurple)" className="float-s1" style={{ transformOrigin: '910px 450px' }} />
      {/* Sphere 5 (Shining cyan sphere near the top-right corner) */}
      <circle cx="1115" cy="75" r="22" fill="url(#sphereGradCyan)" className="float-s2" style={{ transformOrigin: '1115px 75px' }} />
    </svg>
  );

  const renderRoomsHeroGraphic = () => (
    <svg className="absolute inset-0 w-full h-full opacity-90 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" preserveAspectRatio="none">
      <defs>
        <radialGradient id="roomsGlow" cx="75%" cy="35%" r="80%">
          <stop offset="0%" stopColor="#059669" stopOpacity="0.35" />
          <stop offset="45%" stopColor="#064e3b" stopOpacity="0.18" />
          <stop offset="80%" stopColor="#022c22" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#09090b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="emeraldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#064e3b" stopOpacity="0.15" />
        </linearGradient>
        <pattern id="isometricGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
          <line x1="0" y1="0" x2="0" y2="40" stroke="#10b981" strokeWidth="0.75" opacity="0.15" />
          <line x1="0" y1="0" x2="40" y2="0" stroke="#10b981" strokeWidth="0.75" opacity="0.15" />
        </pattern>
      </defs>

      {/* Deep Obsidian canvas background with emerald botanical bleed */}
      <rect width="100%" height="100%" fill="#09090b" />
      <rect width="100%" height="100%" fill="url(#roomsGlow)" />
      
      {/* Structural space/architectural diagonal grid */}
      <rect width="100%" height="100%" fill="url(#isometricGrid)" />

      {/* Header technical boundary */}
      <line x1="40" y1="560" x2="1160" y2="560" stroke="#10b981" strokeWidth="0.5" opacity="0.15" />

      {/* Beautifully detailed cozy bunk beds and room layouts representing hostel sleeping spaces */}
      
      {/* 1. Cozy Hostel Bunk Bed (Dorms Representation) on the left */}
      <g opacity="0.55">
        {/* Bunk Bed main vertical pillars */}
        <rect x="150" y="80" width="8" height="340" rx="3" fill="#10b981" />
        <rect x="360" y="80" width="8" height="340" rx="3" fill="#10b981" />
        
        {/* Ladder rails and rungs */}
        <line x1="120" y1="120" x2="120" y2="420" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
        <line x1="150" y1="120" x2="150" y2="420" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
        <line x1="120" y1="150" x2="150" y2="150" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="190" x2="150" y2="190" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="230" x2="150" y2="230" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="270" x2="150" y2="270" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="310" x2="150" y2="310" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="350" x2="150" y2="350" stroke="#34d399" strokeWidth="2.5" />
        <line x1="120" y1="390" x2="150" y2="390" stroke="#34d399" strokeWidth="2.5" />

        {/* Top Bunk Bed Frame & Bedding */}
        <rect x="150" y="130" width="210" height="30" rx="4" fill="#047857" fillOpacity="0.4" stroke="#34d399" strokeWidth="1.5" />
        {/* Top Bunk Guard Rail */}
        <path d="M 170,130 L 170,110 L 310,110 L 310,130 M 240,110 L 240,130" fill="none" stroke="#10b981" strokeWidth="1.5" />
        {/* Top Bunk Pillow (Fluffy and cozy) */}
        <rect x="310" y="135" width="42" height="20" rx="6" fill="#064e3b" stroke="#34d399" strokeWidth="1.2" />
        <path d="M 310,145 Q 331,135 352,145" fill="none" stroke="#6ee7b7" strokeWidth="0.8" opacity="0.6" />
        {/* Top Bunk Soft Folded Duvet/Blanket */}
        <path d="M 155,140 Q 200,135 250,140 L 250,158 Q 200,158 155,158 Z" fill="#059669" fillOpacity="0.8" stroke="#a7f3d0" strokeWidth="1" />

        {/* Bottom Bunk Bed Frame & Bedding */}
        <rect x="150" y="290" width="210" height="30" rx="4" fill="#047857" fillOpacity="0.4" stroke="#34d399" strokeWidth="1.5" />
        {/* Bottom Bunk Pillow (Fluffy and cozy) */}
        <rect x="310" y="295" width="42" height="20" rx="6" fill="#064e3b" stroke="#34d399" strokeWidth="1.2" />
        <path d="M 310,305 Q 331,295 352,305" fill="none" stroke="#6ee7b7" strokeWidth="0.8" opacity="0.6" />
        {/* Bottom Bunk Soft Folded Duvet/Blanket */}
        <path d="M 155,300 Q 200,295 250,300 L 250,318 Q 200,318 155,318 Z" fill="#059669" fillOpacity="0.8" stroke="#a7f3d0" strokeWidth="1" />
      </g>

      {/* 2. Deluxe Double Bed (Private Room Representation) on the right */}
      <g opacity="0.55">
        {/* Elegant Arched Wooden Headboard */}
        <path d="M 780,310 L 780,110 C 780,100 790,90 800,90 L 980,90 C 990,90 1000,100 1000,110 L 1000,310 Z" fill="none" stroke="#10b981" strokeWidth="1.5" />
        <path d="M 780,125 L 1000,125" stroke="#059669" strokeWidth="1" strokeDasharray="4 2" />
        <path d="M 780,160 L 1000,160" stroke="#059669" strokeWidth="1" strokeDasharray="4 2" />

        {/* Double Bed Mattress Base */}
        <rect x="795" y="140" width="190" height="180" rx="10" fill="#047857" fillOpacity="0.3" stroke="#34d399" strokeWidth="2" />

        {/* Two Plump, Cozy Pillows stacked side-by-side */}
        <g transform="translate(810, 150)">
          {/* Pillow 1 */}
          <rect x="0" y="0" width="70" height="40" rx="10" fill="#064e3b" stroke="#34d399" strokeWidth="1.2" />
          <path d="M 10,20 Q 35,10 60,20" fill="none" stroke="#6ee7b7" strokeWidth="0.8" opacity="0.5" />
          {/* Accent Pillow crease line */}
          <line x1="35" y1="5" x2="35" y2="35" stroke="#34d399" strokeWidth="0.5" opacity="0.3" />
        </g>
        <g transform="translate(900, 150)">
          {/* Pillow 2 */}
          <rect x="0" y="0" width="70" height="40" rx="10" fill="#064e3b" stroke="#34d399" strokeWidth="1.2" />
          <path d="M 10,20 Q 35,10 60,20" fill="none" stroke="#6ee7b7" strokeWidth="0.8" opacity="0.5" />
          {/* Accent Pillow crease line */}
          <line x1="35" y1="5" x2="35" y2="35" stroke="#34d399" strokeWidth="0.5" opacity="0.3" />
        </g>

        {/* Plush Folded Over-Duvet Coverlet with cozy waves */}
        <path d="M 795,210 Q 890,200 990,210 L 990,320 L 795,320 Z" fill="#059669" fillOpacity="0.7" stroke="#6ee7b7" strokeWidth="1.5" />
        <path d="M 795,230 Q 890,220 990,230" fill="none" stroke="#34d399" strokeWidth="1" strokeDasharray="5 3" />
        
        {/* Soft throw blanket at the foot of the bed */}
        <rect x="805" y="275" width="170" height="40" rx="4" fill="#047857" fillOpacity="0.9" stroke="#a7f3d0" strokeWidth="1" />
        <line x1="820" y1="275" x2="820" y2="315" stroke="#a7f3d0" strokeWidth="0.75" strokeDasharray="3 3" />
        <line x1="960" y1="275" x2="960" y2="315" stroke="#a7f3d0" strokeWidth="0.75" strokeDasharray="3 3" />
      </g>

      {/* 3. Cozy Room Accents: Bedside Lamp / Nightstand on both sides of the Double Bed */}
      <g opacity="0.5">
        {/* Left Side Table & Lamp */}
        <rect x="725" y="210" width="40" height="45" rx="3" fill="#064e3b" fillOpacity="0.4" stroke="#10b981" strokeWidth="1" />
        <line x1="745" y1="210" x2="745" y2="195" stroke="#34d399" strokeWidth="2.5" />
        {/* Glowing lamp shade with warm color accent */}
        <path d="M 735,195 L 755,195 L 760,180 L 730,180 Z" fill="#34d399" fillOpacity="0.7" stroke="#10b981" strokeWidth="1" />
        {/* Glow radius representation */}
        <circle cx="745" cy="180" r="15" fill="#10b981" fillOpacity="0.12" className="animate-pulse" />

        {/* Right Side Table & Lamp */}
        <rect x="1015" y="210" width="40" height="45" rx="3" fill="#064e3b" fillOpacity="0.4" stroke="#10b981" strokeWidth="1" />
        <line x1="1035" y1="210" x2="1035" y2="195" stroke="#34d399" strokeWidth="2.5" />
        {/* Glowing lamp shade */}
        <path d="M 1025,195 L 1045,195 L 1050,180 L 1020,180 Z" fill="#34d399" fillOpacity="0.7" stroke="#10b981" strokeWidth="1" />
        <circle cx="1035" cy="180" r="15" fill="#10b981" fillOpacity="0.12" className="animate-pulse" />
      </g>

      {/* Elegant sweeping organic leaf/wave contours mimicking Pechersk Forest hills */}
      <path d="M -100,500 C 200,420 450,560 750,460 C 1050,360 1100,540 1300,480" fill="none" stroke="#10b981" strokeWidth="2.5" opacity="0.22" />
      <path d="M -100,520 C 200,440 450,580 750,480 C 1050,380 1100,560 1300,500" fill="none" stroke="#047857" strokeWidth="1.2" opacity="0.15" strokeDasharray="10 5" />

      {/* Radiant cozy emerald nodes */}
      <circle cx="240" cy="180" r="3" fill="#34d399" className="animate-pulse" />
      <circle cx="500" cy="200" r="2" fill="#34d399" opacity="0.6" />
      <circle cx="850" cy="120" r="4.5" fill="#10b981" opacity="0.35" className="animate-pulse" />
      <circle cx="1080" cy="240" r="3.5" fill="#34d399" opacity="0.4" />
    </svg>
  );

  const renderAboutHeroGraphic = () => (
    <svg className="absolute inset-0 w-full h-full opacity-90 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" preserveAspectRatio="none">
      <defs>
        <radialGradient id="aboutGrowthGlow" cx="20%" cy="80%" r="90%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.25" />
          <stop offset="35%" stopColor="#db2777" stopOpacity="0.15" />
          <stop offset="70%" stopColor="#6366f1" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#09090b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="upwardGrowthGrad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#db2777" stopOpacity="0.15" />
          <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0.75" />
        </linearGradient>
        <pattern id="evolutionGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <line x1="0" y1="40" x2="40" y2="40" stroke="#f59e0b" strokeWidth="0.5" opacity="0.07" />
          <line x1="40" y1="0" x2="40" y2="40" stroke="#f59e0b" strokeWidth="0.5" opacity="0.07" />
        </pattern>
      </defs>

      {/* Deep Obsidian canvas background with golden-rose sunrise growth bleed */}
      <rect width="100%" height="100%" fill="#09090b" />
      <rect width="100%" height="100%" fill="url(#aboutGrowthGlow)" />
      
      {/* Precision evolution grid pattern overlay */}
      <rect width="100%" height="100%" fill="url(#evolutionGrid)" />

      {/* Expanding Concentric Waves of influence propagating from the origin of growth (bottom-left) */}
      <circle cx="100" cy="530" r="150" fill="none" stroke="#db2777" strokeWidth="1" strokeDasharray="4 4" opacity="0.2" />
      <circle cx="100" cy="530" r="300" fill="none" stroke="#f59e0b" strokeWidth="1" strokeDasharray="8 6" opacity="0.15" />
      <circle cx="100" cy="530" r="480" fill="none" stroke="#34d399" strokeWidth="1" strokeDasharray="12 8" opacity="0.12" />
      <circle cx="100" cy="530" r="700" fill="none" stroke="#6366f1" strokeWidth="0.75" opacity="0.08" />
      <circle cx="100" cy="530" r="950" fill="none" stroke="#818cf8" strokeWidth="0.5" opacity="0.05" />

      {/* Fibonacci-inspired progressive vertical growth bars/milestones */}
      <g fill="#f59e0b" opacity="0.08">
        <rect x="200" y="515" width="4" height="15" rx="1" />
        <rect x="280" y="505" width="4" height="25" rx="1" />
        <rect x="380" y="490" width="5" height="40" rx="1.5" />
        <rect x="500" y="470" width="5" height="60" rx="1.5" />
        <rect x="640" y="435" width="6" height="95" rx="2" />
        <rect x="800" y="380" width="6" height="150" rx="2" />
        <rect x="980" y="290" width="7" height="240" rx="2.5" />
        <rect x="1100" y="190" width="8" height="340" rx="3" />
      </g>

      {/* Ascending curves showing the steady trajectory of evolutionary progress */}
      <path d="M 100,530 C 250,510 350,380 550,300 C 750,220 950,150 1120,60" fill="none" stroke="url(#upwardGrowthGrad)" strokeWidth="3" opacity="0.6" />
      <path d="M 100,530 C 200,480 380,440 520,380 C 680,310 850,280 1020,180" fill="none" stroke="#db2777" strokeWidth="1.2" strokeDasharray="6 3" opacity="0.25" />
      <path d="M 100,530 C 300,520 450,420 620,350 C 820,270 950,220 1150,140" fill="none" stroke="#34d399" strokeWidth="1.5" opacity="0.35" />

      {/* Budding/branching offshoots representing new initiatives and creative layers */}
      <path d="M 550,300 C 600,280 680,270 780,280" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.4" />
      <path d="M 750,220 C 800,190 900,180 1050,195" fill="none" stroke="#34d399" strokeWidth="1" strokeDasharray="4 2" opacity="0.35" />

      {/* Milestone Nodes of increasing scale & radiance representing growth stages */}
      
      {/* Milestone 1: Humble beginnings (Seed) */}
      <g transform="translate(100, 530)">
        <circle cx="0" cy="0" r="4" fill="#db2777" />
        <circle cx="0" cy="0" r="8" fill="none" stroke="#db2777" strokeWidth="1" opacity="0.5" className="animate-ping" style={{ animationDuration: '4s' }} />
      </g>

      {/* Milestone 2: Setting roots */}
      <g transform="translate(300, 460)">
        <circle cx="0" cy="0" r="5" fill="#f59e0b" />
        <circle cx="0" cy="0" r="10" fill="none" stroke="#f59e0b" strokeWidth="0.75" opacity="0.4" />
      </g>

      {/* Milestone 3: Early community building */}
      <g transform="translate(550, 300)">
        <circle cx="0" cy="0" r="7" fill="#f59e0b" />
        <circle cx="0" cy="0" r="14" fill="none" stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" className="animate-spin" style={{ animationDuration: '12s' }} />
        <circle cx="0" cy="0" r="18" fill="none" stroke="#f59e0b" strokeWidth="0.5" opacity="0.3" />
      </g>

      {/* Milestone 4: Resilient scaling */}
      <g transform="translate(800, 200)">
        <circle cx="0" cy="0" r="9" fill="#34d399" />
        <circle cx="0" cy="0" r="16" fill="none" stroke="#34d399" strokeWidth="1" opacity="0.6" className="animate-pulse" />
      </g>

      {/* Milestone 5: Future horizons & Full Bloom */}
      <g transform="translate(1120, 60)">
        <circle cx="0" cy="0" r="14" fill="#34d399" className="animate-pulse" style={{ animationDuration: '3s' }} />
        <circle cx="0" cy="0" r="22" fill="none" stroke="#34d399" strokeWidth="1.5" strokeDasharray="4 4" className="animate-spin" style={{ animationDuration: '8s' }} />
        <circle cx="0" cy="0" r="28" fill="none" stroke="#6366f1" strokeWidth="0.75" opacity="0.4" />
      </g>

      {/* Scattered particles representing floating seeds / expanding ideas */}
      <circle cx="180" cy="400" r="2" fill="#db2777" opacity="0.4" />
      <circle cx="420" cy="310" r="2.5" fill="#f59e0b" opacity="0.5" className="animate-pulse" />
      <circle cx="680" cy="210" r="3" fill="#34d399" opacity="0.6" />
      <circle cx="720" cy="140" r="1.5" fill="#f59e0b" opacity="0.4" />
      <circle cx="940" cy="110" r="3.5" fill="#34d399" opacity="0.5" className="animate-pulse" />
      <circle cx="1010" cy="50" r="2" fill="#6366f1" opacity="0.6" />
    </svg>
  );

  const renderContactHeroGraphic = () => (
    <svg className="absolute inset-0 w-full h-full opacity-90 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 600" preserveAspectRatio="none">
      <defs>
        <radialGradient id="contactGlow" cx="50%" cy="50%" r="80%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.32" />
          <stop offset="40%" stopColor="#a78bfa" stopOpacity="0.18" />
          <stop offset="80%" stopColor="#1e1b4b" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#09090b" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="indigoLine" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#818cf8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.1" />
        </linearGradient>
        <pattern id="contactGrid" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
          <circle cx="25" cy="25" r="1" fill="#818cf8" opacity="0.12" />
        </pattern>
      </defs>

      {/* Deep Obsidian canvas background with velvet late-night indigo pub bleed */}
      <rect width="100%" height="100%" fill="#09090b" />
      <rect width="100%" height="100%" fill="url(#contactGlow)" />
      
      {/* Precision layout grid overlay */}
      <rect width="100%" height="100%" fill="url(#contactGrid)" opacity="0.8" />

      {/* Connection web lines linking the various communication channel hubs */}
      <g stroke="url(#indigoLine)" strokeWidth="1.2" opacity="0.28">
        {/* Connector lines between communication hubs */}
        <line x1="180" y1="360" x2="250" y2="160" />
        <line x1="250" y1="160" x2="550" y2="120" />
        <line x1="550" y1="120" x2="480" y2="320" />
        <line x1="480" y1="320" x2="180" y2="360" />
        
        <line x1="550" y1="120" x2="820" y2="180" />
        <line x1="480" y1="320" x2="950" y2="350" />
        <line x1="820" y1="180" x2="950" y2="350" />
        <line x1="820" y1="180" x2="1050" y2="150" />
        <line x1="950" y1="350" x2="1050" y2="150" />
      </g>

      {/* Interactive Communication Hubs & Graphic Icons */}
      
      {/* 1. Phone Call Hub (amber-500 glow) */}
      <g transform="translate(250, 160)" stroke="#fbbf24" className="animate-pulse" style={{ animationDuration: '4s' }}>
        <circle cx="0" cy="0" r="32" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="38" fill="none" strokeWidth="0.5" strokeDasharray="4 4" opacity="0.5" />
        <g transform="translate(-10, -10)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M 2,1 C 2,1 0,3 0,5 C 0,11 5,16 11,16 C 13,16 15,14 15,14 L 12,11 L 10,12 C 10,12 8,11 6,9 C 4,7 3,5 3,5 L 4,3 Z" />
        </g>
      </g>
      
      {/* 2. Email Envelope Hub (indigo-400 glow) */}
      <g transform="translate(180, 360)" stroke="#818cf8">
        <circle cx="0" cy="0" r="30" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="36" fill="none" strokeWidth="0.5" strokeDasharray="6 3" opacity="0.4" />
        <g transform="translate(-14, -10)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <rect x="0" y="0" width="28" height="20" rx="3" />
          <path d="M 0,0 L 14,10 L 28,0" />
        </g>
      </g>

      {/* 3. Social Chat Bubbles Hub (violet-400 glow) */}
      <g transform="translate(550, 120)" stroke="#c084fc">
        <circle cx="0" cy="0" r="34" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="42" fill="none" strokeWidth="0.5" strokeDasharray="3 3" opacity="0.5" />
        <g transform="translate(-13, -11)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M 2,13 C 2,13 0.5,16 0,17 C 2,17 3.5,15 3.5,15 C 4,15.5 5.5,16 7,16 C 11.5,16 15,12.5 15,8 C 15,3.5 11.5,0 7,0 C 2.5,0 0,3.5 0,8 C 0,10 1,12 2,13 Z" />
          <path d="M 12,11 C 11.5,12.5 9,14 7,14 M 13.5,15 C 13.5,15 12.5,16.5 11,17 C 12,17 13.5,16.5 14,16 M 15.5,13.5 C 17,12 18,10 18,8" opacity="0.7" />
        </g>
      </g>

      {/* 4. Telegram-style Instant Messaging Plane Hub (sky-400 glow) */}
      <g transform="translate(480, 320)" stroke="#38bdf8" className="animate-pulse" style={{ animationDuration: '3s' }}>
        <circle cx="0" cy="0" r="32" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="38" fill="none" strokeWidth="0.5" strokeDasharray="5 5" opacity="0.5" />
        <g transform="translate(-12, -12)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M 0,11 L 22,0 L 17,22 L 11,16 L 5,18 L 7,12 Z" />
          <path d="M 7,12 L 17,3 L 11,13" opacity="0.8" />
        </g>
      </g>

      {/* 5. Instagram Camera Hub (pink-400 glow) */}
      <g transform="translate(820, 180)" stroke="#f472b6">
        <circle cx="0" cy="0" r="32" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="38" fill="none" strokeWidth="0.5" strokeDasharray="4 2" opacity="0.4" />
        <g transform="translate(-12, -12)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <rect x="0" y="0" width="24" height="24" rx="6" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="18" cy="6" r="1.5" fill="#f472b6" stroke="none" />
        </g>
      </g>

      {/* 6. Globe / Web Website Hub (emerald-400 glow) */}
      <g transform="translate(1050, 150)" stroke="#34d399">
        <circle cx="0" cy="0" r="30" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="36" fill="none" strokeWidth="0.5" strokeDasharray="4 4" opacity="0.4" />
        <g transform="translate(-11, -11)" strokeWidth="1.8" strokeLinecap="round" fill="none">
          <circle cx="11" cy="11" r="10" />
          <path d="M 1,11 L 21,11" />
          <path d="M 11,1 A 14,14 0 0,0 11,21 A 14,14 0 0,0 11,1" />
          <path d="M 11,1 A 6,6 0 0,0 11,21 A 6,6 0 0,0 11,1" opacity="0.75" />
        </g>
      </g>

      {/* 7. Bell / Push Alert Notification Hub (violet-400 glow) */}
      <g transform="translate(950, 350)" stroke="#a78bfa" className="animate-pulse" style={{ animationDuration: '5s' }}>
        <circle cx="0" cy="0" r="32" fill="#09090b" fillOpacity="0.85" strokeWidth="1.5" />
        <circle cx="0" cy="0" r="40" fill="none" strokeWidth="0.5" strokeDasharray="6 2" opacity="0.4" />
        <g transform="translate(-11, -12)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M 11,2 C 11,2 17,4 17,9 C 17,14 20,16 20,16 L 2,16 C 2,16 5,14 5,9 C 5,4 11,2 11,2 Z" />
          <path d="M 8,19 A 3,3 0 0,0 14,19" />
        </g>
      </g>

      {/* Decorative Wave lines mimicking network signals */}
      <path d="M 100,530 Q 300,450 500,530 T 900,530 T 1150,530" 
            fill="none" stroke="#6366f1" strokeWidth="1.5" opacity="0.15" />
      <path d="M 100,542 Q 300,462 500,542 T 900,542 T 1150,542" 
            fill="none" stroke="#a78bfa" strokeWidth="0.8" strokeDasharray="6 4" opacity="0.12" />
    </svg>
  );

  const isCustomSvg = src === 'custom-crimson-hexagon' || src === 'svg-fallback-universe' || src === 'custom-connection-mesh';
  const isFallback = isCustomSvg;

  return (
    <section id={id} className={`relative overflow-hidden bg-zinc-950 ${className}`}>
      {/* Absolute image background container */}
      <div 
        id={id ? `${id}-bg` : undefined}
        className="absolute inset-0 z-0 bg-zinc-950"
      >
        {/* Layer 1: Generative UI Dark Graphic Overlays (Always rendered as high-end background placeholder) */}
        {src === 'custom-connection-mesh' && renderConnectionMeshGraphic()}
        {src === 'custom-crimson-hexagon' && renderCrimsonHexagonGraphic()}
        {src === 'svg-fallback-universe' && renderHomeHeroGraphic()}
        
        {/* Standard placeholders for other cases */}
        {!isCustomSvg && mediaKey === 'home-hero' && renderConnectionMeshGraphic()}
        {!isCustomSvg && mediaKey === 'rooms-hero' && renderRoomsHeroGraphic()}
        {!isCustomSvg && mediaKey === 'about-hero' && renderAboutHeroGraphic()}
        {!isCustomSvg && mediaKey === 'contact-hero' && renderContactHeroGraphic()}

        {/* Layer 2: Actual Photo Background (fades in cleanly over the vector placeholder) */}
        {!isFallback && (
          <div
            className={`absolute inset-0 transition-opacity duration-1000 ${
              isImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.75)), url("${src}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}

        {/* Hidden image element to handle loading error / fallback swapping robustly */}
        {!isFallback && (
          <img
            src={src}
            onLoad={() => setIsImageLoaded(true)}
            onError={handleError}
            alt=""
            referrerPolicy="no-referrer"
            loading="eager"
            style={{ width: '1px', height: '1px', opacity: 0, position: 'absolute', pointerEvents: 'none' }}
          />
        )}
        
        {/* High-end Shared Fractal Noise layer */}
        {renderSharedNoise()}
      </div>

      {/* Actual Content Area */}
      <div className="relative z-10 w-full h-full">
        {children}
      </div>
    </section>
  );
}
